import * as React from "react";
import { recordLogEntry } from "@/lib/api/request-log";
import { BUILD_ID, fetchServerVersion, reloadForNewVersion } from "@/lib/version";

/**
 * Recovering when a lazy route chunk fails to load.
 *
 * Two very different things look identical at the import layer:
 *
 *   1. A new build shipped while this tab was open. The old hashed chunk names
 *      no longer exist; the SPA fallback answers the request with index.html,
 *      and the import fails with "not a valid JavaScript MIME type". Reloading
 *      fixes this, because fresh HTML carries the new chunk names.
 *
 *   2. The network dropped the file — a flaky connection, a proxy, an
 *      extension. The build is current. Reloading does nothing but loop.
 *
 * The previous version reloaded on a blind 20-second timer, so a case-2 failure
 * dead-ended on a "there's a newer version" screen that wasn't even true. Now we
 * ask the server which build it is actually serving, and only reload when it is
 * genuinely newer — and only once per newer version, so a broken build can never
 * trap a user in a reload loop.
 */

const RELOADED_FOR_KEY = "ro.reloadedForVersion";
const IMPORT_RETRIES = 2;
const RETRY_DELAY_MS = 600;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    /dynamically imported module/i.test(message) ||
    /valid JavaScript MIME type/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Load failed/i.test(message) || // Safari's terse fetch-failure message
    /empty module record/i.test(message) ||
    /_result\.default/.test(message)
  );
}

function alreadyReloadedFor(version: string): boolean {
  try {
    return sessionStorage.getItem(RELOADED_FOR_KEY) === version;
  } catch {
    return false;
  }
}

function markReloadedFor(version: string): void {
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, version);
  } catch {
    /* private mode — worst case is one extra reload, not a loop */
  }
}

/**
 * The one decision point. Returns true when it has committed to a reload (the
 * caller should then stall forever and let the navigation take over); false when
 * reloading would not help and the caller should surface a real error.
 */
async function reloadIfNewBuild(reason: string): Promise<boolean> {
  const server = await fetchServerVersion();

  // Can't tell (offline / dev) or the server is on the same build we are: this
  // is not a stale-deploy problem, so don't reload.
  if (!server || server === BUILD_ID) return false;

  // A newer build exists — but only reload once for it, so a genuinely broken
  // deployment shows an error instead of reloading endlessly.
  if (alreadyReloadedFor(server)) return false;

  markReloadedFor(server);
  recordLogEntry({
    kind: "crash",
    message: `A newer version (${server}) is deployed — reloading to pick it up. (${reason})`,
  });
  reloadForNewVersion();
  return true;
}

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * `React.lazy`, but resilient: reloads for a genuine new deployment, and retries
 * transient network failures before giving up rather than reloading blindly.
 */
export function lazyWithReload<T extends React.ComponentType<Record<string, never>>>(
  factory: () => Promise<{ default: T }>,
) {
  return React.lazy<T>(async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        const module = await factory();

        // A preload failure can resolve with nothing; React would then read
        // `.default` off undefined. Treat that as a chunk failure.
        if (!module || typeof module.default === "undefined") {
          throw new Error("empty module record");
        }
        return module;
      } catch (error) {
        if (!isChunkLoadError(error)) throw error;

        // New build? Reload and never resolve — the navigation takes over.
        if (await reloadIfNewBuild("route chunk")) {
          return await new Promise<{ default: T }>(() => {});
        }

        // Same build: transient. Retry a couple of times before surfacing it,
        // so a momentary blip doesn't need any user action at all.
        if (attempt < IMPORT_RETRIES) {
          await sleep(RETRY_DELAY_MS * (attempt + 1));
          continue;
        }
        throw error;
      }
    }
  });
}

/**
 * Vite fires this when a modulepreload fails. No preventDefault(): that makes
 * Vite resolve the import with undefined, which surfaces the failure from deep
 * inside React instead of as a clean rejection lazyWithReload can handle.
 */
export function installChunkReloadListener() {
  window.addEventListener("vite:preloadError", () => {
    void reloadIfNewBuild("preload");
  });
}
